// Perfect Note - the ONLY module that talks to the API.
//
// web-architecture.md section 4.4 and 7.1: the web client is an ordinary API
// consumer with no privileges a native client would not also have. Keeping every
// request in one file is how that stays true - and the linter enforces it, by
// forbidding `fetch` everywhere else under web/src.
//
// Add a function here for each endpoint you consume. Components call those
// functions; they never construct a URL.

import {
  ApiClientError,
  isRefused,
  isSignedOut,
  messageFor,
  reasonFor,
  refusalRule,
} from './errors.ts';
import { fixtureApi } from './fixtures.ts';
import { ROUTINE_NOTE_CLEAR_HANDOFF_MS } from '../notice.ts';
import { localTimeZone } from '../time-zone.ts';
import type { ExactDecimal } from './exact-decimal.ts';

// Re-exported so components keep importing everything about the API from one
// place; `errors.ts` exists only because `fixtures.ts` refuses things too.
export { ApiClientError, isRefused, isSignedOut, messageFor, reasonFor, refusalRule };

const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api/v1`;

/**
 * Where signing in starts. A URL rather than a function on purpose: this is the
 * one part of the API the client does not CALL. `GET /auth/google/start` answers
 * 302 to Google, and only the browser can follow that - a `fetch` would either
 * chase the redirect itself and land Google's HTML in a promise, or be stopped
 * by CORS, and in neither case does the user reach a Google page to consent on.
 * So the screen renders it as a link and the browser navigates.
 *
 * It still lives here, because this file is where a path under `/api/v1` is
 * allowed to be written down (section 4.4) - a component that spelled the URL
 * itself would be the second place that has to change when the base moves.
 */
export const signInUrl = `${BASE}/auth/google/start`;

/**
 * What `GET /routines` may be narrowed by.
 *
 * `sectionId: 'none'` is the REMINDERS list: a reminder is a routine with no
 * schedule, so there is deliberately no `/reminders` alias - one resource, one
 * URL, and no second place for the two to disagree (prototype-v2.md section
 * 1.8, as shipped). The server answers it in the same order the day screen's
 * block uses, so neither surface sorts anything itself.
 */
export interface RoutineQuery {
  /** A schedule's id, or the word `none` for the things in no schedule. */
  sectionId?: string | 'none';
  /** Archived ones are off every list unless they are asked for by name. */
  includeArchived?: boolean;
}

/** What `GET /sections` may be narrowed by. Archived ones are left out by default. */
export interface SectionQuery {
  /**
   * Include the archived schedules. They are off the day and off every picker,
   * and they are still the account's - so the one screen that offers to put one
   * back has to be able to see it.
   */
  includeArchived?: boolean;
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

/** The plain-file export: one file, and what to call it when it is saved. */
export interface ArchiveFile {
  filename: string;
  blob: Blob;
}

/** What a download should be saved as, out of the header that names it. */
const FILENAME = /filename="([^"]+)"/;

/**
 * The archive's name, or a plain one if the header is not there.
 *
 * The server sends `attachment; filename="perfect-note-2026-08-19.zip"`, and the
 * date in it is the PERSON'S day, resolved in their own stored zone - which is
 * the whole reason this is read off the response rather than made up here. No
 * client in this product works out the account's day (AGENTS.md).
 *
 * The fallback carries no date at all rather than today's: a filename saying the
 * wrong day is worse than one saying no day.
 */
function filenameFrom(disposition: string | null): string {
  return FILENAME.exec(disposition ?? '')?.[1] ?? 'perfect-note-export.zip';
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  const timeZone = localTimeZone();
  try {
    response = await fetch(`${BASE}${path}`, {
      // The session cookie must travel; a native client sends the same token as
      // a bearer header instead, against the same endpoints.
      credentials: 'include',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...(timeZone === null ? {} : { 'Perfect-Note-Time-Zone': timeZone }),
        ...init.headers,
      },
    });
  } catch (cause) {
    throw new ApiClientError(0, 'NETWORK', 'Could not reach Perfect Note.', cause);
  }

  let body: unknown = null;
  if (response.status !== 204) {
    try {
      body = await response.json();
    } catch (cause) {
      if (response.ok) {
        throw new ApiClientError(
          response.status,
          'UNKNOWN_RESPONSE',
          'Perfect Note saved an unreadable response.',
          cause,
          true,
        );
      }
    }
  }

  if (!response.ok) {
    const envelope = (body ?? {}) as ErrorEnvelope;
    throw new ApiClientError(
      response.status,
      envelope.error?.code ?? 'INTERNAL',
      // Clients switch on `code`, never on prose - this text is for humans only.
      envelope.error?.message ?? 'Something went wrong.',
      envelope.error?.details,
      response.status >= 500,
    );
  }

  return body as T;
}

export interface Health {
  status: string;
  database: string;
  uptimeSeconds: number;
}

/**
 * A calendar date as `YYYY-MM-DD`, never an instant. The same string in
 * PostgreSQL, in JSON and here, so nothing converts anywhere (CONTRIBUTING.md).
 */
export type IsoDay = string;

/** One step inside an item. See the note on `DayItem.steps`. */
export interface DayStep {
  id: string;
  title: string;
  done: boolean;
}

/**
 * One row on the day, with everything the approved design draws on it.
 *
 * Section 7.4's item was `id`, `title` and `done` only, while the design
 * (prototype-v1.md section 4, direction A) drew five more things - so each
 * client was left to invent or omit them. They are all specified and all sent
 * now; nothing here is optional, and nothing here is derived by the client.
 */
export interface DayItem {
  id: string;
  title: string;
  done: boolean;
  /** The recurrence in words - "Every day", "Mon, Wed, Fri", "Just once". */
  meta: string;
  /** A finite thing rather than a recurring one; drawn as a small `once` flag. */
  once: boolean;
  /** "This evening" is a quiet marker on the meta line, not a bucket (AGENTS.md). */
  evening: boolean;
  /** Whether there is a note. The glyph is drawn from this, not from the prose. */
  hasNote: boolean;
  /** The item's own note, shown under its steps when it has them. */
  note: string;
  /**
   * Steps count toward the item's own `0/5`, never toward the day's figure: a
   * schedule with five steps is ONE item on the day (AGENTS.md, "Progress counts
   * items, not steps"). Empty for an item with no checklist.
   */
  steps: DayStep[];
}

export interface DaySection {
  id: string;
  name: string;
  /** `#RRGGBB`. Becomes `--c`; the surface tint is derived from it in CSS. */
  colorHex: string;
  /** An icon name; unknown names fall back to a plain circle, as the schema does. */
  symbol: string;
  done: number;
  total: number;
  items: DayItem[];
}

/**
 * A recurrence rule, exactly as the server's `recurrenceSpecSchema` accepts it.
 *
 * `weekdays` is 1 (Sunday) to 7 (Saturday) - the server's numbering, which is
 * one ahead of `weekdayIndex()` in iso-day.ts. The two are converted between in
 * `edit/edit-model.ts` and nowhere else.
 */
export interface RecurrenceSpec {
  freq: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval?: number;
  weekdays?: number[];
  endDay?: IsoDay | null;
}

/** A schedule, as `sectionSchema` sends it. The day payload sends a narrower one. */
export interface Section {
  id: string;
  name: string;
  symbol: string;
  colorHex: string;
  sortIndex: number;
  /** Non-null means archived: off the day, history kept (AGENTS.md). */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A routine, as `routineSchema` sends it - the row behind a day item.
 *
 * The day payload carries the recurrence in WORDS (`DayItem.meta`) and not the
 * rule itself, so an editor that offers to change the repeat has to read the
 * row. That is the whole reason the item sheet fetches one.
 */
export interface Routine {
  id: string;
  /** `item`, `reminder` or `step`, derived server-side from two columns. */
  kind: string;
  sectionId: string | null;
  parentRoutineId: string | null;
  title: string;
  note: string;
  sortIndex: number;
  anchorDay: IsoDay;
  rule: RecurrenceSpec | null;
  dueDay: IsoDay | null;
  evening: boolean;
  /** The board column it was last put in; null is To-do. See `BoardColumn`. */
  status: BoardStatus | null;
  completedDay: IsoDay | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  revision: string;
}

export interface UpdatedRoutine extends Routine {
  previousNote: string | null;
  noteClearOperationId: string | null;
  noteWriteApplied: boolean | null;
}

export interface NewSection {
  name: string;
  symbol?: string;
  colorHex?: string;
}

export interface SectionPatch {
  name?: string;
  symbol?: string;
  colorHex?: string;
  /** false restores an archived schedule, which may hit the cap again. */
  archived?: boolean;
}

export interface NewRoutine {
  /**
   * The schedule it goes in, or NULL for a REMINDER: a routine with no schedule
   * is a reminder (AGENTS.md), and both are the same table and the same route.
   *
   * Required rather than optional even so, and that is the part worth keeping:
   * the server defaults an absent `sectionId` to null, so an omitted field is a
   * silent reminder. Every caller here states which of the two it is making.
   */
  sectionId: string | null;
  title: string;
  note?: string;
  /**
   * A reminder's is the day it was WRITTEN DOWN, and the reminders flow omits
   * it deliberately so the server's own today answers - see `createReminder` in
   * `edit/useEditor.ts`. An item's is the first day its repeat runs from.
   */
  anchorDay?: IsoDay;
  rule?: RecurrenceSpec | null;
  /**
   * A reminder's optional due day. An item in a schedule may not carry one -
   * its schedule already says when it is due - and the server refuses it with
   * `details.rule === 'item-cannot-have-a-due-day'`.
   */
  dueDay?: IsoDay | null;
  evening?: boolean;
}

export interface RoutinePatch {
  title?: string;
  note?: string;
  noteClearOperationId?: string;
  noteClearExpectedNote?: string;
  noteClearExpectedRevision?: string;
  noteRestoreOperationId?: string;
  rule?: RecurrenceSpec | null;
  /** A reminder's optional due day. Null removes it. */
  dueDay?: IsoDay | null;
  evening?: boolean;
  /**
   * Which board column to move a card to - `BoardColumn.status`, sent straight
   * back rather than composed here, so no client knows To-do is a null.
   *
   * It cannot say "complete": that column is reached by writing a COMPLETION,
   * which is `complete()` below and the very same request the day screen's tick
   * makes. See the board block at the foot of this file.
   */
  status?: BoardStatus | null;
  archived?: boolean;
}

/** One reminder on the day - a thing that belongs to no schedule. */
export interface DayReminder {
  id: string;
  title: string;
  note: string;
  dueDay: IsoDay | null;
  /** Ticked on THIS day. A day it was merely open on says false. */
  done: boolean;
  /**
   * It was open on this day and has been finished since, on a later one.
   *
   * Computed server-side so no client derives it, and it is the ONE thing on a
   * past day that is history rather than a control: a reminder is finished once,
   * so ticking this day would be refused (`already-finished`). Drawn with the
   * reverse-clock glyph and no tick at all (captain request, 2026-08-13).
   */
  finishedLater: boolean;
  completedDay: IsoDay | null;
}

// ------------------------------------------------------------ the board -----
//
// A block of its own, kept together, because independently built screens share
// this one API boundary rather than constructing requests themselves.
//
// THE BOARD IS A VIEW OF REMINDERS - migration 0001's decision, at the point
// `routine.status` is defined, not this client's. A card is a routine with no
// schedule: it does not repeat and it is finished for good once.

/**
 * What `routine.status` may hold. Null is not in the union on purpose: it is the
 * absence of a placement rather than a value, and `BoardColumn.status` carries
 * that distinction so no screen here has to know it.
 */
export type BoardStatus = 'in-progress' | 'in-review';

/** One card: a reminder, with only what a card draws. */
export interface BoardCard {
  id: string;
  title: string;
  /**
   * Whether there is a note. The glyph is drawn from this, and THE PROSE DOES
   * NOT TRAVEL here at all - unlike the day item, which draws it. This payload
   * can span an account's reminders rather than one day, so a note of up to
   * 20,000 characters would scale an account-wide read for a glyph. It arrives
   * with the sheet that opens a card, whenever that is built.
   */
  hasNote: boolean;
  dueDay: IsoDay | null;
  /** The day it was finished. Non-null exactly when it is in Complete. */
  completedDay: IsoDay | null;
}

export interface BoardColumn {
  /** `todo`, `in-progress`, `in-review` or `complete`. */
  key: string;
  /** The heading, in the SERVER's words. No screen here writes one of these. */
  label: string;
  /**
   * THE ONE COLUMN REACHED BY A COMPLETION rather than by a status, and the
   * field to switch on. Landing a card here is `complete()`; taking it out is
   * `uncomplete()`. There is no second way to finish something in this product.
   */
  completes: boolean;
  /**
   * What `updateRoutine(id, { status })` writes to land a card in this column,
   * sent straight back rather than composed here. Null on `todo`, because a card
   * nobody has placed IS to-do; null on `complete` too, where it is not read.
   */
  status: BoardStatus | null;
  /** Every card in the column, including completed cards on older pages. */
  total: number;
  cards: BoardCard[];
  /** Opaque position of the next bounded Complete page; null otherwise. */
  nextCursor: string | null;
}

/** The working board and one bounded Complete page. */
export interface BoardResponse {
  /** The user's own today: the day a card completed here is recorded against. */
  today: IsoDay;
  /** All four, always, empty ones included - a board's columns are places. */
  columns: BoardColumn[];
  /** Archived cards: off the columns, still the account's, and restorable. */
  archived: BoardCard[];
}

/** A new card. No `sectionId`, which is what makes it a reminder. */
export interface NewCard {
  title: string;
  /** The column it opens in, from `BoardColumn.status`. */
  status: BoardStatus | null;
}

// ------------------------------------------------------------- notes -------
//
// Kept together, and away from everything above, so that four workstreams in
// this one file collide as little as possible.

/** The schedule an item note sits in. Null for a reminder, and for a plain note. */
export interface NoteSchedule {
  id: string;
  name: string;
  colorHex: string;
  symbol: string;
}

/**
 * One row on the Notes screen, from whichever of the two places it comes from.
 *
 * `kind` is the field everything else turns on. `note` is a row in the `note`
 * table and is written through `/notes/:id`; `item` is `routine.note` - the note
 * on a scheduled item or reminder, which the day payload has carried all along -
 * and is written through `PATCH /routines/:id`. One list, two homes, and the
 * server says which each row is rather than leaving this client to infer it.
 */
export interface Note {
  id: string;
  kind: 'note' | 'item';
  /** The raw title, which is what an editor puts in its field. Empty is possible. */
  title: string;
  body: string;
  /**
   * What a list row draws: the title, or the first line standing in for it, or
   * the one word for a note with nothing in it. Derived on the SERVER, for the
   * reason `meta` and `hasNote` are - two clients must not name one note two
   * ways.
   */
  heading: string;
  /**
   * The user's own calendar day it was last changed - never an instant. Turning
   * one into the other is a time-zone decision, and this client is not where a
   * time zone lives (AGENTS.md).
   */
  updatedDay: IsoDay;
  routineRevision: string | null;
  /** Deleted and recoverable. Only ever true of a `note`. */
  deleted: boolean;
  /** The item, or its schedule, is archived. Only ever true of an `item`. */
  archived: boolean;
  /** Held in the pinned area at the top of the screen. Never true of an `item`. */
  pinned: boolean;
  /**
   * Which NOTE GROUP it is filed in, or null for a note in no group.
   *
   * `noteSectionId` and not `sectionId`: a note group is `note_section`
   * (migration 0008), which is a different table from `section`, the schedules.
   * The prefix travels on the wire so the two can never be read as one - a
   * schedule is capped at ten and drawn on Today, and a note group is neither.
   *
   * Always null on an `item`, deliberately: this field names a manual user
   * section. Routine notes are grouped automatically from durable ownership;
   * manually filing one would need a column on `routine`.
   */
  noteSectionId: string | null;
  /**
   * There is a live read-only SHARE LINK for this note: anyone holding it can
   * read the note without an account.
   *
   * A boolean and never the token. The list is drawn for the OWNER, so what it
   * needs is the badge; the link itself is `noteShare(id)`, asked for once, when
   * somebody opens the note and looks. Putting a credential on every row of a
   * payload that is re-read on every keystroke in the search box would spray it
   * through logs and caches to draw a glyph.
   *
   * IT IS DRAWN, and that is a safety property rather than decoration: people
   * read "unlisted" as "private", so which of your notes a stranger can read has
   * to be visible without opening one.
   *
   * Always false on an `item`: `note_share` references `note`, so a routine
   * note - whether scheduled or a reminder - has nothing to be shared.
   */
  shared: boolean;
  schedule: NoteSchedule | null;
}

/**
 * A read-only share link, as its OWNER sees it. Never sent to anybody else.
 *
 * `url` is built by the SERVER and copied verbatim. A share link is not a screen
 * a client navigates to - it is pasted into somebody else's chat window and has
 * to keep working - so two clients composing their own from the token would
 * publish two different links for one note. This client reads the URL; the only
 * place it writes one down is `share/share-path.ts`, which reads an incoming one
 * back off the address bar.
 */
export interface NoteShare {
  token: string;
  url: string;
  createdAt: string;
}

/**
 * One shared note, as a STRANGER sees it. Three fields, and the list is the
 * feature: no id, no dates, no group, no schedule, and nothing at all about
 * whose note it is.
 */
export interface SharedNote {
  title: string;
  heading: string;
  body: string;
}

/**
 * One group of notes: "Work", "Ideas", or whatever a person invents.
 *
 * NOT a schedule. It has a name, a pin and an archive day, and no colour and no
 * symbol - a schedule has those because the day screen identifies cards by hue,
 * and this is a heading on a list.
 */
export interface NoteSection {
  id: string;
  name: string;
  /** Whether the group sticks to the top of the screen, above the unpinned ones. */
  pinned: boolean;
  /** RFC 3339 UTC while it is archived, null while it is live. */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Everything written down, in one round trip - the same shape the day takes. */
export interface NotesResponse {
  /** The user's own today, so "Yesterday" is the server's answer and not ours. */
  today: IsoDay;
  notes: Note[];
  /**
   * The deleted ones, still recoverable. They travel because an undo that only
   * lived on a toast would expire in ten seconds and leave the note somewhere no
   * screen can reach (web/src/README.md).
   */
  deleted: Note[];
  /**
   * The account's live note groups, ALREADY IN THE ORDER THEY ARE DRAWN -
   * pinned first, then by name.
   *
   * The order is the server's, and this client walks the array rather than
   * sorting it: "pinned groups come first" is a product rule, and two clients
   * must not phrase one rule two ways. They arrive with the notes rather than
   * behind a second read because this screen is one round trip.
   *
   * NOT filtered by the search. A group is a place, not a match - the screen
   * draws whichever groups have a matching note under them.
   */
  sections: NoteSection[];
  /**
   * The archived ones, which still hold their notes. Here for the reason
   * `deleted` is here: a group whose only way back was a toast is a group that
   * is gone, and the notes filed in it are still pointing at it.
   */
  archivedSections: NoteSection[];
}

export interface NewNote {
  title?: string;
  body?: string;
}

export interface NotePatch {
  title?: string;
  body?: string;
  /** false is the undo of a delete. */
  deleted?: boolean;
  pinned?: boolean;
  /**
   * Which group to file it in. `null` takes it out of whichever group it is in,
   * which is why this is nullable rather than only optional: absent means "leave
   * it where it is" and null means "in no group".
   */
  noteSectionId?: string | null;
}

export interface NoteSectionPatch {
  name?: string;
  pinned?: boolean;
  /** false restores an archived group, and every note in it comes back with it. */
  archived?: boolean;
}

// --------------------------------------------------------------- realize ----
//
// A mind map: a topic in the middle and whatever branches off it. Reached from
// inside the Notes tab (`shell/screens.ts`).

/** One mind map, as the list draws it and as the map screen heads itself. */
export interface MindMap {
  id: string;
  /** What somebody typed in the middle. May be empty; `heading` covers that. */
  topic: string;
  /** What a row draws when the topic is empty - the server's word, not ours. */
  heading: string;
  /** Live branches on it, counted by the server so no client counts rows. */
  branches: number;
  updatedDay: IsoDay;
  deleted: boolean;
  /** Identifies the latest delete or restore transition on this map. */
  version: string;
  /** Changes whenever this map or one of its branches is written. */
  writeVersion: string;
}

/**
 * One branch. `parentId` null is a branch off the CENTRE, exactly as a routine
 * with no `sectionId` is a reminder.
 */
export interface MindNode {
  id: string;
  parentId: string | null;
  /** What somebody typed on it. May be empty; `heading` covers that. */
  label: string;
  /** What a box draws - the label, or the server's word for an empty one. */
  heading: string;
  /**
   * Sibling order, and what a top-level branch takes its HUE from. It is not the
   * array position: deleting a branch would then recolour the ones after it,
   * which is a change to branches nobody touched.
   */
  sortIndex: number;
  /**
   * Where somebody DRAGGED it, as an offset from the centre of the map - null
   * when nobody has, which is the default and the common case.
   *
   * Null is what makes dragging additive rather than a mode: a null branch is
   * placed by `realize-model.ts` from the shape of the tree, so a map nobody has
   * touched arranges itself for ever and a map somebody has arranged keeps
   * exactly the boxes they moved. Offsets from the CENTRE rather than page
   * coordinates, because the page is sized to the map and shifts whenever a
   * branch is added anywhere on it.
   */
  x: number | null;
  y: number | null;
  positionSpace: 'model' | 'normalized';
  /** Exact centre-relative anchors used by the shared projection frame. */
  anchorX: ExactDecimal;
  anchorY: ExactDecimal;
  renderDeltaX: ExactDecimal;
  renderDeltaY: ExactDecimal;
  renderOriginX: ExactDecimal;
  renderOriginY: ExactDecimal;
  /** Identifies the latest delete or restore transition on this branch. */
  version: string;
}

/** A deleted branch that can be put back, and how much comes back with it. */
export interface DeletedBranch {
  id: string;
  label: string;
  heading: string;
  /** Nodes restored, this one included. Never zero. */
  restores: number;
  /** Identifies this deletion, so Restore cannot act on a later one. */
  version: string;
}

export interface MapsResponse {
  /** Server-authoritative owner for browser-persisted retry actions. */
  accountId: string;
  /** The user's own today, so "Yesterday" is the server's answer and not ours. */
  today: IsoDay;
  maps: MindMap[];
  /** The deleted ones, still recoverable - the same reason `NotesResponse` sends them. */
  deleted: MindMap[];
}

export interface MapView {
  map: MindMap;
  hasManualPositions: boolean;
  renderScale: number | null;
  renderOriginX: ExactDecimal;
  renderOriginY: ExactDecimal;
  renderSpan: ExactDecimal | null;
  arrangementUndoId: string | null;
  arrangementAction: {
    actionId: string;
    state: 'active' | 'restored' | 'superseded';
  } | null;
  /** Every live branch, ordered by parent and then by sibling order. */
  nodes: MindNode[];
  /**
   * The deleted branches that can be put back - the roots of what was deleted.
   * A deleted branch UNDER a deleted branch is not here: it comes back with its
   * ancestor, and offering it separately would be a button that only refuses.
   */
  deletedBranches: DeletedBranch[];
}

export interface NewBranch {
  actionId: string;
  /** Null puts it on the centre; an id puts it under that branch. */
  parentId: string | null;
  label: string;
}

export interface BranchCreationResult {
  node: MindNode;
  mapVersion: string;
  state: 'live' | 'branch-deleted' | 'map-deleted';
}

export interface BranchPatch {
  label?: string;
  /** false is the undo of a delete, and it brings back exactly that one act. */
  deleted?: boolean;
  /** Required when `deleted` is present. */
  version?: string;
  /** Required with `version`; the map state the action was confirmed against. */
  mapVersion?: string;
  /** Names the transition this request is undoing, when it is an Undo. */
  inverseOf?: string;
  /**
   * Where it was dragged to. BOTH OR NEITHER; Tidy up is the recoverable way
   * to clear manual positions.
   */
  x?: number;
  y?: number;
  positionSpace?: 'model' | 'normalized';
  anchorX?: ExactDecimal;
  anchorY?: ExactDecimal;
  renderDeltaX?: ExactDecimal;
  renderDeltaY?: ExactDecimal;
  renderOriginX?: ExactDecimal;
  renderOriginY?: ExactDecimal;
  actionId?: string;
  renderScale?: number;
  mapRenderOriginX?: ExactDecimal;
  mapRenderOriginY?: ExactDecimal;
  mapRenderSpan?: ExactDecimal;
}

/** One branch's manually chosen place on a map. */
export interface NodePosition {
  id: string;
  x: number;
  y: number;
  positionSpace: 'model' | 'normalized';
  anchorX: ExactDecimal;
  anchorY: ExactDecimal;
  renderDeltaX: ExactDecimal;
  renderDeltaY: ExactDecimal;
  renderOriginX: ExactDecimal;
  renderOriginY: ExactDecimal;
}

// ------------------------------------------- the daily notification --------
//
// ONE optional daily notification, OFF BY DEFAULT, saying how much is on today
// (captain, 2026-08-19). There is deliberately no per-item alarm and no overdue
// chase in this vocabulary, and nothing here names a routine - see
// `api/src/domain/notification.ts`, which is where that decision is written down
// and where a second kind of notification would first have to appear.

/**
 * This browser's setting, as the server holds it.
 *
 * NAMED `Saved…` because the DOM already has a global `PushSubscription` - the
 * object a browser hands back from `pushManager.subscribe()` - and the two are
 * different things: that one is the browser's, this one is the row. A type here
 * that shadowed the global would compile and mean the wrong thing at every use.
 *
 * The browser's encryption keys are deliberately NOT on it. They travel to the
 * server once and are read only by the encryption that sends a notification; a
 * payload that echoed them back would be handing a credential over the wire for
 * no reader.
 */
export interface SavedPushSubscription {
  id: string;
  endpoint: string;
  /**
   * Minutes past the user's OWN local midnight, 0 to 1439. Never a UTC offset:
   * which zone that is resolved in is `app_user.time_zone`, server-side, so
   * somebody who moves country still wakes at eight.
   */
  sendAtMinute: number;
  createdAt: string;
  updatedAt: string;
}

/** What `PUT /push/subscriptions` takes: browser keys and local time. */
export interface NewPushSubscription {
  /** The push service's URL for this browser, from `PushSubscription.endpoint`. */
  endpoint: string;
  /** Base64url, from `getKey('p256dh')`. What the payload is encrypted to. */
  p256dh: string;
  /** Base64url, from `getKey('auth')`. */
  auth: string;
  sendAtMinute: number;
  replacesEndpoint?: string;
}

/** The whole notification setting in one round trip, exactly as the day is. */
export interface PushSettingResponse {
  /**
   * Whether this DEPLOYMENT has VAPID keys at all. False is a supported state,
   * not an error - a development server, or a server whose owner does not want
   * notifications - and the setting says so plainly rather than drawing a toggle
   * that could never fire.
   */
  configured: boolean;
  /** What a browser subscribes with. Null exactly when `configured` is false. */
  publicKey: string | null;
  /** THIS browser's setting, or null - which is off, and is the default. */
  subscription: SavedPushSubscription | null;
}

/** The whole day screen in one round trip - web-architecture.md section 7.4. */
export interface DayResponse {
  day: IsoDay;
  /**
   * The greeting-sized first word of the authenticated Google display name.
   * Empty is meaningful: the greeting drops the name rather than borrowing one
   * from the email address or leaving punctuation behind.
   */
  name: string;
  /** The user's own today, resolved server-side so no client has to guess. */
  today: IsoDay;
  previousDay: IsoDay;
  nextDay: IsoDay;
  progress: { done: number; total: number };
  /**
   * The ACCOUNT's active schedules and the cap they are measured against, both
   * from the database. A day leaves out the schedules with nothing due, so
   * `sections` below cannot be counted to reach `total` - and `cap` travels
   * rather than being restated here, because a client holding its own copy of
   * the number tells the user the wrong maximum the day the cap changes
   * (AGENTS.md, "Ten top-level schedules, capped").
   */
  schedules: { total: number; cap: number };
  sections: DaySection[];
  /**
   * Every reminder that was OPEN on this day - not the two the block draws.
   *
   * The cap of two rows is the day screen's (`reminders/reminders-model.ts`),
   * so this array is the whole set and may be counted, unlike `sections` above.
   * A future day carries none: a day that has not arrived is not a day anything
   * was open on (`assembleDay` in `api/src/domain/day.ts`).
   */
  reminders: DayReminder[];
  /**
   * This account has never made its first-run choice, so it has no content at
   * all and the FIRST-RUN GATE goes up instead of this day.
   *
   * It rides on the day payload rather than being asked for separately because
   * the day read is the first request this client makes: a screen that must not
   * be preceded by the wrong screen cannot wait for a second round trip. The
   * gate's own content is `firstRun()` below, and is fetched only when this is
   * true - so a returning account pays one boolean and no request.
   *
   * Not to be confused with `signedOut`: that one is a state of the SESSION and
   * is raised by any 401. This is a state of the ACCOUNT, and the person is
   * signed in perfectly well.
   */
  firstRun: boolean;
}

// ---------------------------------------------------------- the first run ---
//
// What a brand-new account is offered before it has a day: START WITH THESE, or
// START WITH NOTHING (docs/design/prototype-v1.md section 5, row 6).
//
// The preview rows come from the SERVER for the reason `meta`, `schedules.cap`
// and the board's column labels do: they describe what pressing the button
// actually writes (`firstRunContent` in api/src/domain/first-run.ts), and a
// client holding its own list would go on promising Work, School and Gym after
// the server's content had changed.

/** One schedule the examples would create, as the preview draws it. */
export interface FirstRunExample {
  name: string;
  /** An icon name; unknown names fall back to a plain circle, as elsewhere. */
  symbol: string;
  /** `#RRGGBB`. Becomes `--c` on the chip, exactly as a card's hue does. */
  colorHex: string;
  /** Its items, in one line, in the order they would be written. */
  summary: string;
}

export interface FirstRunResponse {
  /** The choice is still open. False means this account has already answered. */
  pending: boolean;
  /**
   * What to greet them by. Computed server-side so two clients cannot address
   * one person differently; EMPTY IS A REAL VALUE, and the screen drops the
   * name rather than greeting a blank.
   */
  name: string;
  /** Only meaningful while `pending` - it is what the button would write. */
  examples: FirstRunExample[];
}

// ------------------------------------------------------------- the month ----
//
// A calendar month of THIS ACCOUNT's own data, and never a calendar feed: reading
// somebody's Google calendar needs a sensitive scope, which forfeits the
// verification exemption sign-in rests on (AGENTS.md, "No calendar overlay").
//
// The month is counts and the day is content. `GET /months/:month` answers what
// every square of the grid needs and nothing more; what is actually ON one of
// those days is `GET /days/:day`, which this client already has and which the
// month screen calls when a square is opened. Sending every item of every day
// would be thirty-one day payloads to draw thirty-one numbers.

/** A calendar month as `YYYY-MM`. A month is a span of days, never an instant. */
export type IsoMonth = string;

/** One schedule with something due somewhere in the month. */
export interface MonthSection {
  id: string;
  name: string;
  /** `#RRGGBB`. Becomes `--c` on the dot, as it does on a card. */
  colorHex: string;
  symbol: string;
}

/** One day of the month. Every day is here, including the ones with nothing on them. */
export interface MonthDay {
  day: IsoDay;
  /** Items due, counting an item with steps as ONE, as the day screen does. */
  total: number;
  done: number;
  /**
   * The schedules with something due, in the account's order - ids only, which
   * point into `sections` above. The hue and the name travel once rather than
   * once per square.
   */
  sectionIds: string[];
}

/** The whole month screen in one round trip, exactly as the day is. */
export interface MonthResponse {
  month: IsoMonth;
  /** The user's own today, resolved server-side so no client has to guess. */
  today: IsoDay;
  previousMonth: IsoMonth;
  nextMonth: IsoMonth;
  /** The span the month covers, so nothing here counts the days in one. */
  firstDay: IsoDay;
  lastDay: IsoDay;
  /**
   * The whole month's figures. `total` includes the days that have not happened
   * yet, so this is drawn as a COUNT and never as a bar - a month two thirds
   * ahead of itself is not two thirds failed. The day screen makes the same
   * distinction for a future day.
   */
  progress: { done: number; total: number };
  sections: MonthSection[];
  days: MonthDay[];
}

// -------------------------------------------------------------- the year ----
//
// A block of its own, kept together, because this is the file every screen in
// this client has to touch.
//
// THE YEAR'S COUNTS AND HISTORICAL DAYS ARE READ FROM THE COMPLETION LOG, and
// that is the whole difference between it and the day and the month beside it:
// those two answer "what is DUE", expanded from recurrence rules at read time,
// and this one answers "what HAPPENED". Current active schedules may also have
// current-year zero cards. Widening a repeat moves the first two and must move
// nothing here (AGENTS.md, "History is read from the log, never recomputed from
// current rules").
//
// THERE IS NO STREAK IN THIS PAYLOAD and there is no place for one. Every figure
// only ever grows, and a missed day is the absence of an entry - drawn as a pale
// square, never as a broken chain (AGENTS.md, "Counts, never streaks").

/** A calendar year as `YYYY`. Like a month, a span of days and never an instant. */
export type IsoYear = string;

/** One day something was finished on. Days with nothing on them are not sent. */
export interface YearDay {
  day: IsoDay;
  /** Things finished in this schedule on this day. Never zero. */
  finished: number;
}

/** One card on the year: a schedule, and the days it was worked on. */
export interface YearSchedule {
  /**
   * The schedule's id, or NULL when no such schedule exists any more.
   *
   * Null is the deleted-forever case. The completions are real and the schedule
   * they were earned in is gone, so the card is drawn from the log's own
   * snapshots - which is what `completion.routine_id` having no foreign key is
   * for. A card with a null id opens nothing, and nothing here offers to.
   */
  id: string | null;
  /** Its name now if it still exists, or the name it was ticked under if not. */
  name: string;
  symbol: string;
  /** `#RRGGBB`. Becomes `--c` on the tiles, as it does on a card on the day. */
  colorHex: string;
  /** Off the day, history kept. Drawn as a quiet tag on the card. */
  archived: boolean;
  archivedDay: IsoDay | null;
  finished: number;
  /**
   * The days something was finished on, in order. SPARSE, unlike the month's
   * `days`: this screen lays out its own 53-by-7 lattice from `firstDay`, so the
   * quiet days are already accounted for and sending 366 zeroes per schedule
   * would be sending that lattice a second time.
   */
  days: YearDay[];
}

/** One finished reminder, as the reminders card lists it. */
export interface YearReminder {
  /** What it was called when it was finished - the log's own snapshot. */
  title: string;
  day: IsoDay;
}

/** The whole year retrospective, in one round trip, exactly as the day is. */
export interface YearResponse {
  year: IsoYear;
  /** The user's own today, resolved server-side so no client has to guess. */
  today: IsoDay;
  previousYear: IsoYear;
  nextYear: IsoYear;
  /** The span the year covers, so nothing here counts the days in one. */
  firstDay: IsoDay;
  lastDay: IsoDay;
  /** The two figures the year's own sentence is made of. */
  finished: number;
  activeDays: number;
  /**
   * The month with the most finished in it, or null when nothing was.
   *
   * This is the third figure IN PLACE OF a longest streak: the architecture
   * spike computed one, the settled principle forbids it, and what replaced it
   * is a count that only grows (prototype-v1.md section 5).
   */
  busiestMonth: { month: IsoMonth; finished: number } | null;
  /**
   * Current active schedules, including zero cards, plus schedules with history
   * in this year. Every count and sparse day still comes only from the log.
   */
  schedules: YearSchedule[];
  /**
   * A count and the most recent few, and deliberately NO grid: a 53-by-7 grid
   * reads as rhythm, and a reminder is written down once and finished once, so
   * it has no rhythm to show.
   */
  reminders: { finished: number; recent: YearReminder[] };
}

const httpApi = {
  /** GET /api/v1/healthz - a real database round trip on the server. */
  health: (): Promise<Health> => request<Health>('/healthz'),

  /**
   * POST /api/v1/auth/sign-out - and unlike signing IN, this one is an ordinary
   * call rather than a navigation, because nothing has to be consented to.
   *
   * The endpoint revokes the session row AND clears the cookie, in that order,
   * and both halves are required: clearing alone leaves the row renewing itself
   * for another thirty days on every use, and revoking alone leaves the browser
   * sending a dead token (api/src/auth/README.md). So there is nothing for a
   * client to do beyond making the call - and nothing it may do INSTEAD of the
   * call, because the cookie carries `__Host-` and `HttpOnly` and no script can
   * reach it. A client that "signed out" by forgetting locally would leave a
   * live session on the server for a month.
   *
   * A 401 here means the session was already gone, which is the state the caller
   * asked for; the screen that calls this treats it as success for that reason.
   */
  signOut: (): Promise<void> =>
    request<unknown>('/auth/sign-out', { method: 'POST' }).then(() => {}),

  /**
   * GET /api/v1/days/:day - recurrence expanded, completions joined, progress
   * computed and sections ordered, in ONE request (section 7.4).
   *
   * Pass the literal `'today'` and the server resolves it in the user's stored
   * zone, so no client ever has to agree with the server about what day it is.
   */
  day: (day: IsoDay | 'today'): Promise<DayResponse> =>
    request<DayResponse>(`/days/${encodeURIComponent(day)}`),

  // ------------------------------------------------------- first run -------

  /**
   * GET /api/v1/first-run - the greeting and the preview the gate draws.
   *
   * Asked for only when a day payload said `firstRun`, so nobody who has already
   * answered ever pays for it.
   */
  firstRun: (): Promise<FirstRunResponse> => request<FirstRunResponse>('/first-run'),

  /**
   * POST /api/v1/first-run - the choice, once and for all.
   *
   * `seed: true` writes the three example schedules; `false` leaves the account
   * empty. Either way the choice is settled and the gate comes down for good.
   *
   * NOT IDEMPOTENT, unlike `complete()`: a second call answers 409 with
   * `details.rule === 'first-run-already-chosen'` rather than quietly agreeing,
   * because "Start with these" succeeding on an account that chose to start with
   * nothing would be a claim about content that is not there. That refusal is
   * the ordinary answer to a retry after a lost response, so a caller treats it
   * as "the choice is settled" and goes to the day - never as a failure.
   */
  chooseFirstRun: (seed: boolean): Promise<{ seeded: boolean }> =>
    request<{ seeded: boolean }>('/first-run', {
      method: 'POST',
      body: JSON.stringify({ seed }),
    }),

  /**
   * GET /api/v1/months/:month - a whole calendar month of counts, in ONE
   * request, computed from the same rules the day is (see the block above).
   *
   * Pass the literal `'this'` and the server resolves it in the user's own
   * stored zone, exactly as `day()` passes `'today'`. A client that worked out
   * which month it is would first have to work out which day it is, and that is
   * the one thing no client in this system does.
   */
  month: (month: IsoMonth | 'this'): Promise<MonthResponse> =>
    request<MonthResponse>(`/months/${encodeURIComponent(month)}`),

  /**
   * GET /api/v1/years/:year - the year retrospective, in ONE request, read from
   * the completion log rather than expanded from any rule (see the block above).
   *
   * Pass the literal `'this'` and the server resolves it in the user's own
   * stored zone, exactly as `day()` passes `'today'` and `month()` passes
   * `'this'`. No client here works out which year it is, because that would mean
   * first working out which day it is.
   */
  year: (year: IsoYear | 'this'): Promise<YearResponse> =>
    request<YearResponse>(`/years/${encodeURIComponent(year)}`),

  /**
   * POST /api/v1/completions - ticking something off. Idempotent by design
   * (`ON CONFLICT DO NOTHING`): 201 on create, 200 on repeat, so a phone on a
   * flaky connection can retry safely (section 7.2).
   */
  complete: (routineId: string, day: IsoDay): Promise<{ created: boolean }> =>
    request<{ created: boolean }>('/completions', {
      method: 'POST',
      body: JSON.stringify({ routineId, day }),
    }),

  /**
   * DELETE /api/v1/completions - un-ticking. The exact inverse of `complete()`:
   * the same identity tuple, and idempotent for the same reason - unticking
   * something already not ticked is the state the caller asked for, not a
   * failure (section 7.2).
   *
   * The tuple travels in the QUERY STRING rather than in a body, which is the
   * one place this differs from the POST. A body on DELETE is legal and widely
   * dropped - by proxies, and by more than one HTTP client a native app might
   * use - and a delete whose parameters silently vanish in transit is the last
   * request that should be shaped that way.
   */
  uncomplete: (routineId: string, day: IsoDay): Promise<{ deleted: boolean }> =>
    request<{ deleted: boolean }>(
      `/completions?${new URLSearchParams({ routineId, day }).toString()}`,
      { method: 'DELETE' },
    ),

  // ------------------------------------------------------- schedules -------
  //
  // DELETE archives and `?permanent=true` deletes forever, on both resources
  // (AGENTS.md). Archiving is the destructive default because it is the one
  // that can be undone, and undo on every destructive action is a correctness
  // requirement here rather than a feature.

  /** GET /api/v1/sections - the ACCOUNT's schedules, including the ones with
   *  nothing due today, which the day payload leaves out. */
  sections: (query: SectionQuery = {}): Promise<Section[]> =>
    request<{ sections: Section[] }>(
      query.includeArchived === true ? '/sections?includeArchived=true' : '/sections',
    ).then((body) => body.sections),

  /** POST /api/v1/sections - 409 with `details.rule === 'schedule-cap'` at the cap. */
  createSection: (input: NewSection): Promise<Section> =>
    request<{ section: Section }>('/sections', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((body) => body.section),

  updateSection: (id: string, patch: SectionPatch): Promise<Section> =>
    request<{ section: Section }>(`/sections/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then((body) => body.section),

  /** Off the day, history kept. Undone by `updateSection(id, {archived: false})`. */
  archiveSection: (id: string): Promise<void> =>
    request<unknown>(`/sections/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => {}),

  /**
   * Gone, with its items, in one transaction.
   *
   * `?permanent=true` is what reaches `deleteSectionForever()`, and the items
   * going too is the point: `section_id` is `on delete set null`, so a schedule
   * deleted without them leaves every item behind as a routine with no
   * schedule - which is a REMINDER (AGENTS.md). This client must never delete a
   * schedule any other way, and must never clear an item's `sectionId` itself.
   */
  deleteSectionForever: (id: string): Promise<void> =>
    request<unknown>(`/sections/${encodeURIComponent(id)}?permanent=true`, {
      method: 'DELETE',
    }).then(() => {}),

  // ----------------------------------------------------------- items -------

  /** GET /api/v1/routines/:id - the row behind a day item, rule included. */
  routine: (id: string): Promise<Routine> =>
    request<{ routine: Routine }>(`/routines/${encodeURIComponent(id)}`).then(
      (body) => body.routine,
    ),

  createRoutine: (input: NewRoutine): Promise<Routine> =>
    request<{ routine: Routine }>('/routines', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((body) => body.routine),

  updateRoutine: (id: string, patch: RoutinePatch): Promise<UpdatedRoutine> =>
    request<{
      routine: Routine;
      previousNote: string | null;
      noteWriteApplied: boolean | null;
    }>(`/routines/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then(({ routine, previousNote, noteWriteApplied }) => ({
      ...routine,
      previousNote,
      noteClearOperationId: patch.noteClearOperationId ?? patch.noteRestoreOperationId ?? null,
      noteWriteApplied,
    })),

  startRoutineNoteClearUndo: (id: string, operationId: string): Promise<boolean> =>
    request<{ started: boolean }>(
      `/routines/${encodeURIComponent(id)}/note-clears/${encodeURIComponent(operationId)}/undo-window`,
      { method: 'POST' },
    ).then(({ started }) => started),

  retireRoutineNoteClear: (id: string, operationId: string): Promise<boolean> =>
    request<{ retired: boolean }>(
      `/routines/${encodeURIComponent(id)}/note-clears/${encodeURIComponent(operationId)}`,
      { method: 'DELETE' },
    ).then(({ retired }) => retired),

  archiveRoutine: (id: string): Promise<void> =>
    request<unknown>(`/routines/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(() => {}),

  deleteRoutineForever: (id: string): Promise<void> =>
    request<unknown>(`/routines/${encodeURIComponent(id)}?permanent=true`, {
      method: 'DELETE',
    }).then(() => {}),

  // ------------------------------------------------------- reminders -------
  //
  // A reminder is a routine with no schedule, so it has no endpoints of its
  // own: it is CREATED by `createRoutine({ sectionId: null })`, TICKED by the
  // same `complete()` every other row uses, and REMOVED by `archiveRoutine()`.
  // Only the listing needed anything new, and it is the same `/routines` the
  // items use with one query value.

  /**
   * GET /api/v1/routines - a schedule's items, or `sectionId: 'none'` for the
   * account's reminders, in the order the day screen's block draws them.
   *
   * The order is the SERVER'S: worst-overdue first, then due today, then dated,
   * then undated, with a finished one sunk below the open ones (`byReminderOrder`
   * in `api/src/domain/day.ts`). The full list and the block on the day are the
   * same rows in the same order because they are one comparator applied on the
   * server, rather than two copies of one rule that can drift apart - so nothing
   * in this client sorts reminders.
   */
  routines: (query: RoutineQuery = {}): Promise<Routine[]> => {
    const params = new URLSearchParams();
    if (query.sectionId !== undefined) params.set('sectionId', query.sectionId);
    if (query.includeArchived === true) params.set('includeArchived', 'true');
    const search = params.toString();
    return request<{ routines: Routine[] }>(`/routines${search === '' ? '' : `?${search}`}`).then(
      (body) => body.routines,
    );
  },

  // ----------------------------------------------------------- board -------
  //
  // TWO FUNCTIONS, AND THAT IS THE POINT. Everything else the board does it
  // does through the calls above, which existed before it did:
  //
  //   moving a card       updateRoutine(id, { status })
  //   completing one      complete(id, board.today)   <- the day screen's tick
  //   taking it back out  uncomplete(id, card.completedDay)
  //   archiving one       archiveRoutine(id), undone by updateRoutine(id, {archived:false})
  //
  // There is deliberately no `completeCard`: a second way to finish something
  // is a second thing that can disagree with the log the year retrospective is
  // read from (api/src/domain/board.ts).

  /** GET /api/v1/board - the working board and one bounded Complete page. */
  board: (completeCursor?: string): Promise<BoardResponse> => {
    const params = new URLSearchParams();
    if (completeCursor !== undefined) params.set('completeCursor', completeCursor);
    const search = params.toString();
    return request<BoardResponse>(`/board${search === '' ? '' : `?${search}`}`);
  },

  /**
   * POST /api/v1/routines with no `sectionId`, which is what makes a REMINDER
   * (AGENTS.md) - the thing a card on the board is.
   *
   * Separate from `createRoutine` above rather than loosening that one further:
   * `NewRoutine` takes a null `sectionId` since the reminders flow writes one,
   * but it carries no `status`, and a card has to open in the column it was
   * dropped into.
   */
  createCard: (input: NewCard): Promise<Routine> =>
    request<{ routine: Routine }>('/routines', {
      method: 'POST',
      body: JSON.stringify({ title: input.title, status: input.status }),
    }).then((body) => body.routine),

  // ----------------------------------------------------------- notes -------
  //
  // The Notes screen is ONE read, the way the day is: the notes of their own,
  // the notes written on items, the deleted ones, and the user's today - so no
  // client assembles that screen out of several calls, and none of them decides
  // which of two lists is newer.
  //
  // There is deliberately no function here for writing an ITEM's note.
  // `updateRoutine(id, { note })` above already does it and has since the day
  // screen shipped; a second one would be a second answer to "what may write
  // `routine.note`". A row's `kind` is what says which of the two to call.

  /** GET /api/v1/notes - everything written down, newest first. */
  notes: (search = ''): Promise<NotesResponse> =>
    request<NotesResponse>(
      search === '' ? '/notes' : `/notes?${new URLSearchParams({ search }).toString()}`,
    ),

  // `signal` on the two WRITES an editor makes without being asked to, and on
  // neither of the deletes: an autosave is the one request in this client that
  // can still be in the air when the person changes their mind about the note it
  // is carrying, so it is the one that has to be cancellable.

  createNote: (input: NewNote, signal?: AbortSignal): Promise<Note> =>
    request<{ note: Note }>('/notes', {
      method: 'POST',
      body: JSON.stringify(input),
      signal,
    }).then((body) => body.note),

  updateNote: (id: string, patch: NotePatch, signal?: AbortSignal): Promise<Note> =>
    request<{ note: Note }>(`/notes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
      signal,
    }).then((body) => body.note),

  /**
   * Recoverable. Undone by `updateNote(id, { deleted: false })`, which is what
   * the Undo on the toast sends - and the row that comes back is the one the
   * caller just deleted, so nothing has to be read again to offer that.
   */
  deleteNote: (id: string): Promise<Note> =>
    request<{ note: Note }>(`/notes/${encodeURIComponent(id)}`, { method: 'DELETE' }).then(
      (body) => body.note,
    ),

  /** Gone. The one action on this screen with no way back, so it asks first. */
  deleteNoteForever: (id: string): Promise<void> =>
    request<unknown>(`/notes/${encodeURIComponent(id)}?permanent=true`, {
      method: 'DELETE',
    }).then(() => {}),

  // --------------------------------------------------------- realize -------
  //
  // TWO READS, NOT ONE, and it is the one place this client's screens differ
  // from the day, the notes and the board. `maps()` is the list you choose
  // from; `map(id)` is the one you are working in. Sending every branch of every
  // map to draw a list of topics would grow with the whole history of the
  // account, and nothing on the list draws a branch.

  /** GET /api/v1/realize - the account's maps, the deleted ones, and today. */
  maps: (): Promise<MapsResponse> => request<MapsResponse>('/realize'),

  /** GET /api/v1/realize/maps/:id - one map, its branches, and what can come back. */
  map: (id: string): Promise<MapView> =>
    request<MapView>(`/realize/maps/${encodeURIComponent(id)}`),

  createMap: (topic: string, actionId: string, expectedAccountId: string): Promise<MindMap> =>
    request<{ map: MindMap }>('/realize/maps', {
      method: 'POST',
      body: JSON.stringify({ topic, actionId, expectedAccountId }),
    }).then((body) => body.map),

  /** The topic in the middle, and putting a deleted map back. */
  updateMap: (
    id: string,
    patch: {
      topic?: string;
      deleted?: boolean;
      version?: string;
      writeVersion?: string;
      inverseOf?: string;
    },
  ): Promise<MindMap> =>
    request<{ map: MindMap }>(`/realize/maps/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then((body) => body.map),

  /** Recoverable. `inverseOf` is present only when this request is an Undo. */
  deleteMap: (
    id: string,
    version: string,
    writeVersion: string,
    inverseOf?: string,
  ): Promise<MindMap> => {
    const query = new URLSearchParams({ version, writeVersion });
    if (inverseOf !== undefined) query.set('inverseOf', inverseOf);
    return request<{ map: MindMap }>(
      `/realize/maps/${encodeURIComponent(id)}?${query.toString()}`,
      { method: 'DELETE' },
    ).then((body) => body.map);
  },

  createBranch: (mapId: string, input: NewBranch): Promise<BranchCreationResult> =>
    request<BranchCreationResult>(`/realize/maps/${encodeURIComponent(mapId)}/nodes`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateBranch: (
    id: string,
    patch: BranchPatch,
  ): Promise<{
    node: MindNode;
    mapVersion: string;
    renderScale: number | null;
    renderOriginX: ExactDecimal;
    renderOriginY: ExactDecimal;
    renderSpan: ExactDecimal | null;
    hasManualPositions: boolean;
    arrangementUndoId: string | null;
    arrangementAction: MapView['arrangementAction'];
    view: MapView | null;
  }> =>
    request<{
      node: MindNode;
      mapVersion: string;
      renderScale: number | null;
      renderOriginX: ExactDecimal;
      renderOriginY: ExactDecimal;
      renderSpan: ExactDecimal | null;
      hasManualPositions: boolean;
      arrangementUndoId: string | null;
      arrangementAction: MapView['arrangementAction'];
      view: MapView | null;
    }>(`/realize/nodes/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  restoreBranch: (
    id: string,
    version: string,
    mapVersion: string,
    inverseOf?: string,
  ): Promise<{ node: MindNode; mapVersion: string; view: MapView }> =>
    request<{ node: MindNode; mapVersion: string; view: MapView }>(
      `/realize/nodes/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ deleted: false, version, mapVersion, inverseOf }),
      },
    ),

  /**
   * A branch and everything under it, recoverably.
   *
   * `removed` is how many went, counted by the server: the sentence on the toast
   * says "and the 3 under it", and a client counting the tree it happens to be
   * holding would say something else after a re-read. `inverseOf` is present only
   * when this request reverses a restore rather than starting a fresh deletion.
   */
  deleteBranch: (
    id: string,
    version: string,
    mapVersion: string,
    inverseOf?: string,
  ): Promise<{ node: MindNode; removed: number; view: MapView }> => {
    const query = new URLSearchParams({ version, mapVersion });
    if (inverseOf !== undefined) query.set('inverseOf', inverseOf);
    return request<{ node: MindNode; removed: number; view: MapView }>(
      `/realize/nodes/${encodeURIComponent(id)}?${query.toString()}`,
      { method: 'DELETE' },
    );
  },

  tidyMap: (
    mapId: string,
    actionId: string,
    mapVersion: string,
  ): Promise<{
    undoId: string | null;
    undoState: 'active' | 'restored' | 'none';
    view: MapView;
  }> =>
    request<{
      undoId: string | null;
      undoState: 'active' | 'restored' | 'none';
      view: MapView;
    }>(`/realize/maps/${encodeURIComponent(mapId)}/arrangement`, {
      method: 'POST',
      body: JSON.stringify({ action: 'tidy', actionId, mapVersion }),
    }),

  restoreArrangement: (mapId: string, undoId: string): Promise<MapView> =>
    request<{ undoId: null; undoState: 'restored'; view: MapView }>(
      `/realize/maps/${encodeURIComponent(mapId)}/arrangement`,
      {
        method: 'POST',
        body: JSON.stringify({ action: 'undo', undoId }),
      },
    ).then((body) => body.view),

  // ------------------------------------------------------ note groups -------
  //
  // Four writes and NO READ, because `notes()` above already answers it: the
  // groups travel with the notes that go in them, so the screen is still one
  // round trip. Which group a NOTE is in is `updateNote(id, { noteSectionId })`
  // - a property of the note, written where the note is written, rather than a
  // membership list on the group.

  /**
   * GET /api/v1/export - everything the account holds, as one zip of plain
   * files. See `api/src/routes/export.routes.ts`.
   *
   * THE ONE CALL IN THIS FILE THAT DOES NOT GO THROUGH `request`, because
   * `request` reads a JSON envelope and this answers with bytes. Everything
   * else about it is the same, deliberately: the same `credentials: 'include'`,
   * the same envelope read out of a failure, and therefore the same 401 that
   * raises the signed-out screen.
   *
   * A FETCH RATHER THAN A LINK, which is worth saying because signing IN is the
   * opposite (`signInUrl` above). A plain `<a href>` would let the browser
   * stream the file straight to disk and never bother this client - but a 401
   * would then arrive as a page of JSON in a new tab instead of the signed-out
   * screen, and a 500 as a blank one. The archive is single-digit megabytes at
   * the worst realistic size, so holding it as a Blob costs less than losing
   * every failure this app knows how to draw.
   *
   * The name comes back in `Content-Disposition`, which a same-origin response
   * exposes, because only the server knows which day it is where the person is.
   */
  exportArchive: async (): Promise<ArchiveFile> => {
    let response: Response;
    try {
      response = await fetch(`${BASE}/export`, { credentials: 'include' });
    } catch (cause) {
      throw new ApiClientError(0, 'NETWORK', 'Could not reach Perfect Note.', cause);
    }

    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null);
      const envelope = (body ?? {}) as ErrorEnvelope;
      throw new ApiClientError(
        response.status,
        envelope.error?.code ?? 'INTERNAL',
        envelope.error?.message ?? 'Something went wrong.',
        envelope.error?.details,
      );
    }

    return {
      filename: filenameFrom(response.headers.get('content-disposition')),
      blob: await response.blob(),
    };
  },

  createNoteSection: (name: string): Promise<NoteSection> =>
    request<{ section: NoteSection }>('/note-sections', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }).then((body) => body.section),

  updateNoteSection: (id: string, patch: NoteSectionPatch): Promise<NoteSection> =>
    request<{ section: NoteSection }>(`/note-sections/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }).then((body) => body.section),

  /**
   * Archives it. THE NOTES IN IT ARE NOT TOUCHED - they stay filed, and are
   * drawn ungrouped while the group is away, so the undo is
   * `updateNoteSection(id, { archived: false })` and nothing else.
   */
  archiveNoteSection: (id: string): Promise<NoteSection> =>
    request<{ section: NoteSection }>(`/note-sections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).then((body) => body.section),

  /** Gone, and its notes are not: they become notes in no group. */
  deleteNoteSectionForever: (id: string): Promise<void> =>
    request<unknown>(`/note-sections/${encodeURIComponent(id)}?permanent=true`, {
      method: 'DELETE',
    }).then(() => {}),

  // ------------------------------------------------------ the share link -----
  //
  // THE ONE PART OF THIS API THAT PUBLISHES A NOTE TO SOMEBODY WITH NO ACCOUNT.
  // Three calls belong to the owner and require the session exactly as
  // everything above does; the fourth is read BY the stranger and the endpoint
  // accepts no credential but the token in its own path. A signed-in browser may
  // still attach its cookie automatically, but the public route never reads it.
  //
  // Sharing is off until somebody presses the button. There is no call here that
  // turns it on for anything other than one named note, and there is deliberately
  // none that lists every shared note or turns them all off - the Notes list
  // already answers "which of mine are published" through `Note.shared`.

  /** The link this note already has, or null. Never mints one. */
  noteShare: (id: string): Promise<NoteShare | null> =>
    request<{ share: NoteShare | null }>(`/notes/${encodeURIComponent(id)}/share`).then(
      (body) => body.share,
    ),

  /**
   * Publish it, or hand back the link it already has.
   *
   * IDEMPOTENT: a second press, a retry after a lost response and two tabs all
   * answer with the SAME link. A note reachable by two live URLs is a note whose
   * owner cannot turn it off in one act, so the server refuses to mint a second
   * (migration 0010's `unique` on `note_id`).
   */
  shareNote: (id: string): Promise<NoteShare> =>
    request<{ share: NoteShare }>(`/notes/${encodeURIComponent(id)}/share`, {
      method: 'POST',
    }).then((body) => body.share),

  /**
   * Stop. The link is dead on the very next request.
   *
   * NO UNDO IS OFFERED ANYWHERE FOR THIS, which is the one exception to "undo on
   * every destructive action" in this client - and it is deliberate. What is
   * destroyed is a credential rather than writing: the note is untouched, so
   * there is nothing to put back, and an undo that revived the same URL would be
   * the opposite of what somebody pressing "Stop sharing" is asking for. Sharing
   * again makes a DIFFERENT link, and the screen says so before the press.
   */
  revokeNoteShare: (id: string): Promise<void> =>
    request<unknown>(`/notes/${encodeURIComponent(id)}/share`, { method: 'DELETE' }).then(() => {}),

  /**
   * Read a shared note. THE ONE CALL WHOSE ENDPOINT REQUIRES NO SESSION.
   *
   * The token in the path is the whole credential, so it is encoded like any
   * other path segment even though `newShareToken()` only ever produces
   * url-safe characters - the encoding is what keeps a hand-typed or truncated
   * link from becoming a different request.
   *
   * A wrong token, a revoked one and a link to a deleted note all answer 404
   * with the same body; the page turns that into words rather than ranking the
   * reasons (`share/SharedNote.tsx`).
   */
  sharedNote: (token: string): Promise<SharedNote> =>
    request<{ note: SharedNote }>(`/shared-notes/${encodeURIComponent(token)}`).then(
      (body) => body.note,
    ),

  // ------------------------------------------- the daily notification -------
  //
  // THE RESOURCE IS A BROWSER, NOT AN ACCOUNT. A Web Push subscription is
  // issued to one browser by that browser's push service; a phone and a laptop
  // are two of them, each with its own time. Its endpoint identifies every
  // request about an existing row; the GET may omit it before this browser has
  // ever subscribed. Nothing here invents a device identifier.
  //
  // There is deliberately no function that SENDS one: delivery is the server's
  // scheduled job and there is no route a client could call to make this server
  // push anything (`api/src/routes/push.routes.ts`).

  /**
   * GET /api/v1/push - whether this deployment has notifications at all, the
   * key a browser subscribes with, and what THIS browser's setting is, in one
   * round trip - the same shape the day and the notes screens take.
   *
   * `endpoint` is omitted by a browser that has never subscribed, which has
   * none to send and still has to be told whether the feature exists here.
   */
  pushSetting: (endpoint?: string): Promise<PushSettingResponse> =>
    request<PushSettingResponse>(
      endpoint === undefined ? '/push' : `/push?${new URLSearchParams({ endpoint }).toString()}`,
    ),

  /**
   * PUT /api/v1/push/subscriptions - turn it on for this browser, or move its
   * time. Idempotent by design (an upsert on the endpoint), because a browser
   * re-subscribes on its own after a permission change or a service-worker
   * update and this is sent again every time.
   */
  subscribePush: (input: NewPushSubscription): Promise<SavedPushSubscription> =>
    request<{ subscription: SavedPushSubscription }>('/push/subscriptions', {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then((body) => body.subscription),

  /**
   * DELETE /api/v1/push/subscriptions - turn it off for this browser. The tuple
   * is in the QUERY STRING for the reason `uncomplete()` puts its own there.
   *
   * `deleted: false` means this browser was already off, which is the state the
   * caller asked for and never a failure.
   */
  unsubscribePush: (endpoint: string): Promise<{ deleted: boolean }> =>
    request<{ deleted: boolean }>(
      `/push/subscriptions?${new URLSearchParams({ endpoint }).toString()}`,
      { method: 'DELETE' },
    ),
};

/**
 * Development only, and only when asked for: `VITE_USE_FIXTURES=1 npm run dev`
 * serves the seeded day from `fixtures.ts` instead of the network, so the screen
 * can be built and looked at before the data routes exist. `import.meta.env.DEV`
 * is a build-time constant, so a production build drops this branch and the
 * fixtures with it.
 */
const USE_FIXTURES = import.meta.env.DEV && import.meta.env.VITE_USE_FIXTURES === '1';

const selectedApi = USE_FIXTURES ? { ...httpApi, ...fixtureApi() } : httpApi;
const routineNoteWrites = new Map<string, Promise<void>>();
const latestRoutineNoteWrites = new Map<string, symbol>();
const routineNoteWriteMarkers = new WeakMap<UpdatedRoutine, symbol>();
const ROUTINE_NOTE_RETRY_MS = 250;

type RoutineNoteClearCompletion = 'recoverable' | 'unavailable' | 'superseded';

function routineNoteOutcome(
  routine: Routine,
  operationId: string,
  applied: boolean,
): UpdatedRoutine {
  return {
    ...routine,
    previousNote: null,
    noteClearOperationId: operationId,
    noteWriteApplied: applied,
  };
}

function waitForRoutineNoteRetry(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isDefinitiveRoutineNoteFailure(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    ((error.status >= 400 && error.status < 500) || (error.status >= 500 && !error.ambiguous))
  );
}

function isDefinitiveRoutineNoteUndoWindowFailure(error: unknown): boolean {
  return error instanceof ApiClientError && error.status >= 400 && error.status < 500;
}

export function requireAppliedRoutineNoteWrite(updated: UpdatedRoutine): UpdatedRoutine {
  if (updated.noteWriteApplied !== false) return updated;
  throw new ApiClientError(
    409,
    'CONFLICT',
    'That note changed after this edit began. Reopen it before changing it again.',
  );
}

async function startRoutineNoteClearUndo(id: string, operationId: string): Promise<boolean> {
  const deadline = Date.now() + ROUTINE_NOTE_CLEAR_HANDOFF_MS;
  let retryImmediately = true;
  for (;;) {
    try {
      const started = await selectedApi.startRoutineNoteClearUndo(id, operationId);
      return Date.now() < deadline && started;
    } catch (error) {
      if (isDefinitiveRoutineNoteUndoWindowFailure(error)) throw error;
      const remaining = deadline - Date.now();
      if (remaining <= 0) return false;
      if (retryImmediately) {
        retryImmediately = false;
      } else {
        await waitForRoutineNoteRetry(Math.min(ROUTINE_NOTE_RETRY_MS, remaining));
      }
    }
  }
}

function currentRoutineNoteWrite(updated: UpdatedRoutine): boolean {
  const marker = routineNoteWriteMarkers.get(updated);
  return marker !== undefined && latestRoutineNoteWrites.get(updated.id) === marker;
}

async function completeRoutineNoteClear(
  updated: UpdatedRoutine,
): Promise<RoutineNoteClearCompletion> {
  const operationId = updated.noteClearOperationId;
  if (operationId === null || !currentRoutineNoteWrite(updated)) return 'superseded';

  let started: boolean;
  try {
    started = await startRoutineNoteClearUndo(updated.id, operationId);
  } catch (error) {
    if (!currentRoutineNoteWrite(updated)) return 'superseded';
    throw error;
  }
  if (!currentRoutineNoteWrite(updated)) return 'superseded';
  return started ? 'recoverable' : 'unavailable';
}

async function routine(id: string): Promise<Routine> {
  const preceding = routineNoteWrites.get(id);
  if (preceding !== undefined) await preceding;
  return selectedApi.routine(id);
}

function updateRoutine(id: string, patch: RoutinePatch): Promise<UpdatedRoutine> {
  if (patch.note === undefined) return selectedApi.updateRoutine(id, patch);

  const clearing = patch.note.trim() === '';
  if (
    clearing &&
    (patch.noteClearExpectedNote === undefined || patch.noteClearExpectedRevision === undefined)
  ) {
    return Promise.reject(
      new ApiClientError(
        400,
        'BAD_REQUEST',
        'A note clear needs the exact note revision the edit began with.',
      ),
    );
  }
  const operationId = clearing
    ? (patch.noteClearOperationId ?? crypto.randomUUID())
    : (patch.noteRestoreOperationId ?? null);
  const sent = clearing ? { ...patch, noteClearOperationId: operationId ?? undefined } : patch;

  const execute = async (): Promise<UpdatedRoutine> => {
    if (operationId === null) return selectedApi.updateRoutine(id, sent);

    const deadline = Date.now() + ROUTINE_NOTE_CLEAR_HANDOFF_MS;
    let attempted = false;
    let retryImmediately = true;
    for (;;) {
      if (attempted && Date.now() >= deadline) {
        const routine = await selectedApi.routine(id);
        return routineNoteOutcome(
          routine,
          operationId,
          patch.noteRestoreOperationId !== undefined && routine.note === patch.note,
        );
      }
      attempted = true;
      try {
        return await selectedApi.updateRoutine(id, sent);
      } catch (error) {
        if (isDefinitiveRoutineNoteFailure(error)) throw error;
        const remaining = deadline - Date.now();
        if (remaining <= 0) continue;
        if (retryImmediately) {
          retryImmediately = false;
        } else {
          await waitForRoutineNoteRetry(Math.min(ROUTINE_NOTE_RETRY_MS, remaining));
        }
      }
    }
  };

  const preceding = routineNoteWrites.get(id);
  const requested = preceding === undefined ? execute() : preceding.then(execute);
  const write = requested.then((updated) => {
    if (
      updated.noteWriteApplied !== false &&
      (!clearing || (updated.previousNote !== null && updated.previousNote !== ''))
    ) {
      const marker = Symbol();
      latestRoutineNoteWrites.set(id, marker);
      routineNoteWriteMarkers.set(updated, marker);
    }
    return updated;
  });
  const settled = write.then(
    () => undefined,
    () => undefined,
  );
  routineNoteWrites.set(id, settled);
  void settled.then(() => {
    if (routineNoteWrites.get(id) === settled) routineNoteWrites.delete(id);
  });
  return write;
}

export const api = {
  ...selectedApi,
  routine,
  updateRoutine,
  startRoutineNoteClearUndo,
  completeRoutineNoteClear,
};
